import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService, WidgetComponent, WidgetStyle } from '@sdbn/common';
import { ContextChange } from '@sdbn/common';

import { MasterDetailListEntry, SimpleMasterDetailListEntry, TranslationService } from '@sdbn/common';
import { ResponseMessage } from '@sdbn/common';
import { ResponseMessageType } from '@sdbn/common';
import { ResponseStatus } from '@sdbn/common';
import { EditableTransportOptionConstraint, TransportOption, TransportOptionConfiguration, TransportOptionConstraint } from '../../transport-options.model';
import { AbstractWidgetComponent } from '@sdbn/common';
import { TransportOptionsAdministrationContext } from '../../transport-options-administration.context';
import { TransportOptionsAdministrationService } from '../../transport-options-administration.service';
import { DictionaryEntry, LanguageValue } from '@sdbn/common';

@Component({
    selector: 'app-transport-options-administration',
    templateUrl: './transport-options-administration.component.html',
    styleUrls: ['./transport-options-administration.component.scss'],
    standalone: false
})
export class TransportOptionsAdministrationComponent extends AbstractWidgetComponent implements OnInit, WidgetComponent {

  editMode = 0;

  responseMessages: ResponseMessage[];
  loading = false;

  newOptionId = -1;

  optionsContext = new TransportOptionsAdministrationContext();

  /** Drag and Drop fields */
  draggedOption: TransportOption;


  data = [
    new SimpleMasterDetailListEntry({ key: 'structure', listTitleDictionaryKey: 'tmc-yard.ui.admin.transport-options-administration.view-mode.structure' }),
    new SimpleMasterDetailListEntry({ key: 'constraints', listTitleDictionaryKey: 'tmc-yard.ui.admin.transport-options-administration.view-mode.constraints' }),
    new SimpleMasterDetailListEntry({ key: 'timeframepriority', listTitleDictionaryKey: 'tmc-yard.ui.admin.transport-options-administration.view-mode.time-frame-priority' })
  ];
  currentView: string = 'struktur';
  current = this.data[0];

  constructor(
    public translationService: TranslationService,
    private dialog: MatDialog,
    private transportOptionsAdministrationService: TransportOptionsAdministrationService,
    private dialogService: DialogService
  ) {
    super();
    this.loadOptionConfiguration();
  }

  public ngOnInit() { }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  public onAddOptionStart(event: DragEvent) {
    event.dataTransfer.effectAllowed = "copy";
    // IOS geht nur wenn gesetzt
    event.dataTransfer.setData("text/plain", "");

    this.draggedOption = new TransportOption();
  }

  public onDragOptionEnd(event: DragEvent) {
    this.draggedOption = null;
  }

  setView(index: number) {
    this.editMode = index;
  }

  public cancel() {

    this.dialogService.showConfirm(
      'tmc-yard.service.admin.transport-option-administration.confirm-dialog.cancel.title',
      'tmc-yard.service.admin.transport-option-administration.confirm-dialog.cancel.message'
    ).afterClosed().subscribe(confirm => {
      if (confirm) {
        this.loadOptionConfiguration();
      }
    });

  }

  public save() {

    if (this.checkForDuplicateNames()) {
      const message = new ResponseMessage({});
      message.messageType = ResponseMessageType.FAILURE;
      message.dictionaryKey = 'tmc.admin.transport-options.error.option-name-exists';
      this.responseMessages.push(message);
      return;
    }

    this.loading = true;
    this.responseMessages = [];

    const optionsToSave = new Array<TransportOption>();
    optionsToSave.push(...this.optionsContext.options);
    optionsToSave.push(...this.optionsContext.deletedOptions);

    const constraintsToSave = new Array<TransportOptionConstraint>();
    for (const c of this.optionsContext.constraints) {
      constraintsToSave.push(new TransportOptionConstraint(c));
    }
    for (const c of this.optionsContext.deletedConstraints) {
      constraintsToSave.push(new TransportOptionConstraint(c));
    }

    const configuration = new TransportOptionConfiguration({ transportOptions: optionsToSave, constraints: constraintsToSave });

    this.transportOptionsAdministrationService.saveTransportOptions(configuration).subscribe(
      result => {
        if (result.status === ResponseStatus.SUCCESS) {
          this.loadOptionConfiguration();
        }
        this.responseMessages = result.responseMessages;
        this.loading = false;
      },
      error => {
        this.loading = false;
      }
    );

  }


  private loadOptionConfiguration() {

    this.loading = true;
    this.optionsContext.reset();
    this.responseMessages = [];

    this.transportOptionsAdministrationService.loadOptionConfiguration().subscribe(
      result => {
        const configuration = new TransportOptionConfiguration(result);
        this.optionsContext.setOptions(configuration.transportOptions, 'transport-options-administration');
        const editableConstraints = new Array<EditableTransportOptionConstraint>();
        for (const c of configuration.constraints) {
          editableConstraints.push(new EditableTransportOptionConstraint(c));
        }
        editableConstraints.sort((a, b) => {
          return a.name > b.name ? 1 : a.name === b.name ? 0 : -1;
        });
        this.optionsContext.setConstraints(editableConstraints, 'transport-options-administration')
        this.loading = false;
      },
      error => {
        this.loading = false;
      }
    );

  }

  private checkForDuplicateNames(): boolean {

    const parentOptions = this.optionsContext.options.filter(o => o.parentId == null);

    // Prüfen ob es doppelte namen auf der parent eben gibt
    if (this.checkForDuplicateNamesInOptionList(parentOptions)) {
      return true;
    }

    // Prüfen ob es doppelte namen innerhalb der children eines parent gibt
    for (const parent of parentOptions) {

      const childOptions = this.optionsContext.options.filter(o => o.parentId === parent.id);

      if (this.checkForDuplicateNamesInOptionList(childOptions)) {
        return true;
      }
    }

    return false;
  }

  private checkForDuplicateNamesInOptionList(options: Array<TransportOption>): boolean {

    const optionsNames = new Array<DictionaryEntry>();
    options.forEach(
      o => optionsNames.push(o.name)
    );

    const languagesSet = new Set<string>();
    const languageValues = new Array<LanguageValue>();

    for (const de of optionsNames) {
      for (const v of de.values) {
        // fetch all available languages to check
        languagesSet.add(v.language);
        // flatten all values
        languageValues.push(v);
      }
    }

    // workaround because we can not loop over Set wihtout ES6 configured
    const languages = new Array<string>();
    languagesSet.forEach(l => {
      languages.push(l);
    });

    for (const language of languages) {
      const valuesForLanguage = new Set();

      for (const nameValue of languageValues) {
        if (nameValue.language === language) {
          if (!valuesForLanguage.has(nameValue.value)) {
            valuesForLanguage.add(nameValue.value);
          } else {
            return true;
          }
        }
      }
    }
    return false;
  }

  handleSelectionChange(entry: MasterDetailListEntry) {
    this.currentView = entry['key'];
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
